import { useState } from 'react'
import { api, ErroApi } from '../lib/api'
import { Marca } from '../componentes/Marca'
import './conta-fluxo.css'

const MINIMA = 10

/**
 * Definir a senha nova, com o token do e-mail.
 *
 * É a URL que a API **já emite** (`conta.ts`, no corpo do e-mail de
 * recuperação) e que até hoje caía na home. O token vale uma hora e
 * queima no primeiro uso.
 *
 * ─── O AVISO NÃO É DETALHE ────────────────────────────────────────
 *
 * `app.trocar_senha` revoga TODAS as sessões, inclusive a de quem está
 * trocando. Quem faz isso do celular e tem o consultório aberto no
 * computador vai ser deslogado lá também. Dizer isso antes é o que
 * separa "o sistema me expulsou" de "eu pedi isso" — e, para quem
 * desconfia de invasão, é exatamente o efeito desejado.
 */
export function NovaSenha({ token, aoEntrar }: { token: string; aoEntrar(): void }) {
  const [senha, setSenha] = useState('')
  const [repetir, setRepetir] = useState('')
  const [mostrando, setMostrando] = useState(false)
  const [enviando, setEnviando] = useState(false)
  const [pronto, setPronto] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const curta = senha.length > 0 && senha.length < MINIMA
  const diferem = repetir.length > 0 && senha !== repetir
  const podeEnviar = senha.length >= MINIMA && senha === repetir && !enviando

  async function enviar(e: React.FormEvent) {
    e.preventDefault()
    setEnviando(true); setErro(null)
    try {
      await api('/v1/conta/senha', { metodo: 'POST', corpo: { token, senha } })
      setPronto(true)
    } catch (err) {
      setErro(err instanceof ErroApi && err.codigo === 'TOKEN_INVALIDO'
        ? 'Este link expirou ou já foi usado. Peça outro na tela de login.'
        : 'Não foi possível trocar a senha agora. Tente de novo em instantes.')
    } finally { setEnviando(false) }
  }

  if (pronto) {
    return (
      <main className="cf">
        <div className="cf__caixa cartao">
          <Marca tamanho={26} />
          <h1>Senha trocada</h1>
          <p className="ajuda">
            Todos os aparelhos que estavam conectados foram desconectados.
            Entre de novo com a senha que você acabou de criar.
          </p>
          <button className="botao" onClick={aoEntrar}>Ir para o login</button>
        </div>
      </main>
    )
  }

  return (
    <main className="cf">
      <div className="cf__caixa cartao">
        <Marca tamanho={26} />
        <h1>Criar uma senha nova</h1>
        <p className="ajuda">Mínimo de {MINIMA} caracteres. Use algo que só você saiba.</p>

        {erro && <div className="aviso aviso--erro" role="alert">{erro}</div>}

        <form onSubmit={enviar} className="cf__form">
          <div className="campo">
            <div className="cf__rotulo-linha">
              <label htmlFor="ns-senha">Senha nova</label>
              <button type="button" className="cf__revelar"
                      onClick={() => setMostrando(!mostrando)}
                      aria-pressed={mostrando}>
                {mostrando ? 'Ocultar' : 'Mostrar'}
              </button>
            </div>
            <input id="ns-senha" className="entrada"
                   type={mostrando ? 'text' : 'password'} required autoFocus
                   autoComplete="new-password" minLength={MINIMA}
                   aria-invalid={curta} aria-describedby="ns-senha-ajuda"
                   value={senha} onChange={(e) => setSenha(e.target.value)} />
            <span id="ns-senha-ajuda" className={curta ? 'ajuda ajuda--erro' : 'ajuda'}>
              {curta ? `${senha.length} de ${MINIMA} caracteres, no mínimo.`
                     : `${MINIMA} caracteres ou mais.`}
            </span>
          </div>

          <div className="campo">
            <label htmlFor="ns-repetir">Repita a senha</label>
            <input id="ns-repetir" className="entrada"
                   type={mostrando ? 'text' : 'password'} required
                   autoComplete="new-password"
                   aria-invalid={diferem} aria-describedby="ns-repetir-ajuda"
                   value={repetir} onChange={(e) => setRepetir(e.target.value)} />
            <span id="ns-repetir-ajuda" className={diferem ? 'ajuda ajuda--erro' : 'ajuda'}>
              {diferem ? 'As duas não são iguais.' : 'Para conferir que não houve erro de digitação.'}
            </span>
          </div>

          <div className="aviso aviso--atencao" role="note">
            Trocar a senha desconecta <strong>todos os aparelhos</strong>, inclusive este.
            É o que você quer se desconfia que alguém entrou na sua conta.
          </div>

          <button className="botao" type="submit" disabled={!podeEnviar}>
            {enviando ? 'Trocando…' : 'Trocar a senha'}
          </button>
        </form>
      </div>
    </main>
  )
}
